import React, { useEffect, useState } from 'react';
import api from '../api';

const TOGGLES = [
  {
    name: 'read_cookies',
    label: 'Read cookie files',
    description: "Downloads every cookie file of each new infostealer log. Only used to raise the risk score; turned off, the risk score is calculated without cookies.",
  },
  {
    name: 'read_history',
    label: 'Read browser history',
    description: 'Downloads the browser history files of each new infostealer log, up to the limit below. Only used to find monitored slugs in visited sites; turned off, slugs are only searched in saved passwords.',
    setting: {
      name: 'max_history_files',
      label: 'History files per log',
      description: 'Most history files read from each log (one per browser and profile). Each file costs one file/read credit.',
      min: 1,
      max: 1000,
    },
  },
];

const FILE_READ_KINDS = [
  { key: 'file/read:passwords', label: 'passwords' },
  { key: 'file/read:cookies', label: 'cookies' },
  { key: 'file/read:history', label: 'history' },
  { key: 'file/read:system', label: 'system' },
  { key: 'file/read:software', label: 'software' },
  { key: 'file/read:autofill', label: 'autofill' },
];

const OTHER_REQUESTS = [
  { key: 'file/treeview', label: 'file/treeview' },
];

const cellStyle = { padding: '8px 10px', borderBottom: '1px solid var(--border)', fontVariantNumeric: 'tabular-nums' };
const headStyle = { ...cellStyle, fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-light)', textAlign: 'left' };

// e.g. "history 20 · cookies 9 · passwords 1 · treeview 1", largest first
const formatLogRequests = (requests) => Object.entries(requests || {})
  .sort(([, a], [, b]) => b - a)
  .map(([key, count]) => `${key === 'file/treeview' ? 'treeview' : key.replace('file/read:', '')} ${count}`)
  .join(' · ');

const IntelxPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await api.getIntelxStatus());
    } catch (err) {
      setError(`Could not load the IntelX status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line
  }, []);

  const handleToggle = async (name) => {
    const enabled = !status.toggles[name];
    setSaving(name);
    setError(null);
    try {
      const data = await api.updateIntelxToggle(name, enabled);
      setStatus({ ...status, toggles: data.toggles });
    } catch (err) {
      setError(`Could not change the setting: ${err.message}`);
    } finally {
      setSaving(null);
    }
  };

  const handleSetting = async (setting) => {
    const value = Number(drafts[setting.name]);
    if (!Number.isInteger(value) || value < setting.min || value > setting.max) {
      setError(`${setting.label} must be a whole number between ${setting.min} and ${setting.max}.`);
      return;
    }
    setSaving(setting.name);
    setError(null);
    try {
      const data = await api.updateIntelxSetting(setting.name, value);
      setStatus({ ...status, settings: data.settings });
      setDrafts(({ [setting.name]: _, ...rest }) => rest);
    } catch (err) {
      setError(`Could not change the setting: ${err.message}`);
    } finally {
      setSaving(null);
    }
  };

  const usage = status?.usage || {};
  const fileReads = FILE_READ_KINDS.reduce((total, kind) => total + (usage[kind.key] || 0), 0);

  const renderSetting = (setting, active) => {
    const saved = status.settings?.[setting.name];
    const draft = drafts[setting.name];
    const value = draft !== undefined ? draft : saved ?? '';
    const changed = draft !== undefined && Number(draft) !== saved;
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); handleSetting(setting); }}
        style={{ marginTop: 10, opacity: active ? 1 : 0.55 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor={`setting-${setting.name}`} style={{ fontWeight: 500, color: 'var(--text)', fontSize: '0.9rem', marginRight: 4 }}>{setting.label}</label>
          <input
            id={`setting-${setting.name}`}
            type="number"
            className="form-input"
            min={setting.min}
            max={setting.max}
            step={1}
            value={value}
            disabled={!active || saving !== null}
            onChange={(e) => setDrafts({ ...drafts, [setting.name]: e.target.value })}
            style={{ width: 90, padding: '4px 8px', fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}
          />
          {changed && (
            <button
              type="submit"
              className="btn"
              disabled={!active || saving !== null}
              style={{ fontSize: 13, padding: '5px 14px', background: 'var(--primary-color)', color: 'var(--secondary-color)' }}
            >
              {saving === setting.name ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 4 }}>
          {active ? setting.description : 'Only applies while history reading is on.'}
        </div>
      </form>
    );
  };

  return (
    <div style={{ background: 'var(--white)', borderRadius: 12, boxShadow: '0 2px 8px rgba(30,39,56,0.07)', padding: '18px 24px', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: 'var(--text)', fontWeight: 600, fontSize: '1.15rem' }}>IntelX</h3>
        <button
          type="button"
          className="btn btn-link"
          style={{ fontSize: 14, padding: '6px 12px', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', textDecoration: 'none', cursor: 'pointer' }}
          onClick={fetchStatus}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="error-message mb-3" style={{ fontSize: '0.875rem', padding: '6px 10px' }}>{error}</div>}

      {status && (
        <>
          <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
            {TOGGLES.map(toggle => {
              const enabled = status.toggles[toggle.name];
              return (
                <div key={toggle.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <button
                    type="button"
                    id={`toggle-${toggle.name}`}
                    role="switch"
                    aria-checked={enabled}
                    onClick={() => handleToggle(toggle.name)}
                    disabled={saving !== null}
                    title={enabled ? `Turn off: ${toggle.label}` : `Turn on: ${toggle.label}`}
                    style={{
                      flex: '0 0 auto',
                      width: 48,
                      height: 24,
                      marginTop: 2,
                      borderRadius: 12,
                      border: 'none',
                      background: enabled ? 'var(--primary-color)' : '#ccc',
                      cursor: saving !== null ? 'wait' : 'pointer',
                      position: 'relative',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'white',
                      position: 'absolute',
                      top: 3,
                      left: enabled ? 27 : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <label htmlFor={`toggle-${toggle.name}`} style={{ cursor: 'pointer' }}>
                      <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                        {toggle.label} <span style={{ fontWeight: 400, color: 'var(--text-light)' }}>{enabled ? 'On' : 'Off'}</span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>{toggle.description}</div>
                    </label>
                    {toggle.setting && renderSetting(toggle.setting, enabled)}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <div style={{ flex: '1 1 340px', minWidth: 0, overflowX: 'auto' }}>
            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Logs with the most requests</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginBottom: 6 }}>
              Top 10 infostealer logs by file/read and treeview requests, over the same period
            </div>
            {status.top_logs?.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={headStyle}>Log</th>
                    <th style={{ ...headStyle, width: '32%' }}>Last seen in</th>
                    <th style={{ ...headStyle, width: 80, textAlign: 'right' }}>Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {status.top_logs.map(log => (
                    <tr key={log.systemid}>
                      <td style={{ ...cellStyle, verticalAlign: 'top' }}>
                        <div title={log.source || log.systemid} style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.source || 'Unknown source'}
                        </div>
                        <div title={log.systemid} style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.systemid}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{formatLogRequests(log.requests)}</div>
                      </td>
                      <td style={{ ...cellStyle, verticalAlign: 'top', color: 'var(--text-light)', overflowWrap: 'anywhere' }}>{log.context || '-'}</td>
                      <td style={{ ...cellStyle, verticalAlign: 'top', textAlign: 'right', fontWeight: 600 }}>{log.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: '0.9rem', color: 'var(--text-light)' }}>No log files downloaded yet in this period.</div>
            )}
          </div>
            <div style={{ flex: '1 1 300px', minWidth: 0, overflowX: 'auto' }}>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Requests made by OSIRIS</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginBottom: 6 }}>
                {status.usage_since
                  ? `Since IntelX last restored the credits (counting from ${new Date(status.usage_since).toLocaleString()})`
                  : 'Since IntelX last restored the credits'}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <tbody>
                  <tr>
                    <td style={{ ...cellStyle, fontFamily: 'monospace', fontWeight: 600 }}>file/read</td>
                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{fileReads}</td>
                  </tr>
                  {FILE_READ_KINDS.filter(kind => kind.key !== 'file/read:autofill' || usage[kind.key]).map(kind => (
                    <tr key={kind.key}>
                      <td style={{ ...cellStyle, paddingLeft: 26, color: 'var(--text-light)' }}>{kind.label}</td>
                      <td style={{ ...cellStyle, textAlign: 'right', color: 'var(--text-light)' }}>{usage[kind.key] || 0}</td>
                    </tr>
                  ))}
                  {OTHER_REQUESTS.map(request => (
                    <tr key={request.key}>
                      <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{request.label}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{usage[request.key] || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IntelxPanel;
